import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine } from "@/components/motion";
import { EmailGate } from "@/components/EmailGate";
import { IMAGES } from "@/constants/site";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={IMAGES.hero} alt="CNCroutersoft G-code plotter showing a live toolpath" className="w-full h-[120%] object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0 blueprint-grid opacity-40" />

      <motion.div style={{ opacity: fade }} className="relative z-10 flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-20 pt-28 pb-16 max-w-[1400px] w-full mx-auto">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="label-mono mb-6 flex items-center gap-3"
          data-testid="hero-kicker"
        >
          <span className="inline-block w-8 h-px bg-[#FF3B00]" />
          CAM software for the home workshop — v1.0.0
        </motion.p>

        <h1 className="font-display font-bold uppercase leading-[0.9] tracking-tighter text-[clamp(3rem,9vw,8.5rem)]" data-testid="hero-headline">
          <MaskedLine delay={0.35}>Draw the shape.</MaskedLine>
          <MaskedLine delay={0.5}><span className="text-[#FF3B00]">Watch the cut.</span></MaskedLine>
          <MaskedLine delay={0.65}><span className="text-stroke">Ship the G-code.</span></MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 max-w-lg text-base md:text-lg text-white/70 leading-relaxed"
          data-testid="hero-sub"
        >
          CNCroutersoft pairs a shape editor with a real-time toolpath plotter, then exports
          machine-ready G-code. Free for hobbyists and small shops — Windows only.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-10"
          id="download"
        >
          <EmailGate />
        </motion.div>
      </motion.div>

      <div className="relative z-10 border-t border-white/10 bg-black/40 backdrop-blur-sm">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20 py-4 flex flex-wrap gap-x-10 gap-y-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-white/40" data-testid="hero-stats">
          <span>Real-time plotter</span>
          <span>G-code export</span>
          <span className="text-[#F5A623]">Windows 10/11 only</span>
          <span className="hidden sm:inline">Free for personal use</span>
        </div>
      </div>
    </section>
  );
};
